'use client';
import { Fragment, useMemo, useState } from 'react';
import { AddCircleOutline, Search } from '@eqi-investimentos/icons/web';
import {
  Button,
  Divider,
  Flex,
  Text,
  Input,
  Skeleton,
} from '@eqi-investimentos/react';
import { useT } from '@/libs/i18n/utils/useT';
import { useInvest } from '@/contexts/InvestContext';
import { Thumb } from '@/components/elements/Thumb';
import { normalizeString } from '@/utils/format';
import { IfRender, MapRender } from '@/utils/jsx';
import { useGetWalletConnectors } from '../../http/client/useGetWalletConnectors';

export const InstitutionsToConnectList = () => {
  const t = useT();

  const { closeConnectorsDrawer } = useInvest();
  const [search, setSearch] = useState('');

  const { data: connectors = [], isLoading } = useGetWalletConnectors();

  const filteredConnectors = useMemo(() => {
    if (isLoading) return [];
    if (search.length > 0) {
      const normalizedSearch = String(
        normalizeString(search).toLocaleLowerCase().trim()
      );
      return connectors.filter(({ name }) => {
        const normalizedName = String(
          normalizeString(name).toLocaleLowerCase()
        );
        return normalizedName.includes(normalizedSearch);
      });
    }
    return connectors;
  }, [connectors, isLoading, search]);

  const openPluggyWidget = (connectorId?: string) => {
    closeConnectorsDrawer();
    const connector = filteredConnectors.find(
      (connector) => connector?.id === connectorId
    );
    window.dispatchEvent(
      new CustomEvent('loaded-pluggy-widget', {
        detail: { connector },
      })
    );
  };

  return (
    <Flex flexDir="column" gap="2xl">
      <Text size="2xl">
        {t('screens.myWallet.drawers.connectorsBrokers.subTitle')}
      </Text>
      <Input
        type="text"
        w="full"
        h="3.5rem"
        borderRadius="xs"
        p="m"
        bg="background.primary"
        borderColor="border.primary"
        fontSize="l"
        color="content.tertiary"
        placeholder={t(
          'screens.myWallet.drawers.connectorsBrokers.placeholderSearch'
        )}
        value={search}
        onChange={({ target: { value } }) => setSearch(value)}
        rightIcon={<Search boxSize="1.5rem" color="content.brand" />}
      />

      <Flex flexDir="column" h="full" gap="l">
        <IfRender condition={isLoading}>
          <MapRender
            items={[...Array(8)]}
            render={(idx) => <Skeleton key={idx} w="full" h="1rem" />}
          />
        </IfRender>
        <IfRender condition={!isLoading}>
          <MapRender
            items={filteredConnectors}
            render={({ id, thumb, name }) => (
              <Fragment key={id}>
                <Button
                  key={id}
                  variant="naked"
                  onClick={() => openPluggyWidget(id)}
                  justifyContent="space-between"
                  alignItems="center"
                  p="0"
                  w="full"
                  color="content.secondary"
                >
                  <Flex alignItems="center" gap="m">
                    <Thumb
                      src={thumb}
                      alt={`instituicao ${name}`}
                      width={40}
                      height={40}
                      radius={6}
                    />
                    <Text
                      maxW="10.375rem"
                      maxH="1.6875rem"
                      size="l"
                      fontWeight="semibold"
                      whiteSpace="wrap"
                      textOverflow="ellipsis"
                      overflow="hidden"
                      noOfLines={2}
                    >
                      {name}
                    </Text>
                  </Flex>
                  <Flex alignItems="center" gap="2xs">
                    <Text size="l" color="content.brand">
                      {t('attributes.connect')}
                    </Text>
                    <AddCircleOutline
                      width={15}
                      height={15}
                      color="content.brand"
                    />
                  </Flex>
                </Button>
                <Divider borderColor="border.secondary" />
              </Fragment>
            )}
          />
        </IfRender>
      </Flex>
    </Flex>
  );
};
